import { Search, X } from "lucide-react";
import type { FilterState, SortKey } from "@/lib/board/filters";
import { LABEL_CHIP } from "@/lib/board/look";
import type { Label } from "@/lib/board/types";

const SORT_LABEL: Record<SortKey, string> = {
  manual: "Board order",
  updated: "Recently updated",
  due: "Due date",
  priority: "Priority",
  created: "Newest",
};

export function FilterBar({
  filters, labels, matchCount, filtering, archivedCount, onQuery, onLabel, onSort, onCompare, onToggleArchived, onClear,
}: {
  filters: FilterState;
  labels: Label[];
  matchCount: number | null;
  filtering: boolean;
  /** Cards taken off the board. Shown on the button so the archive is findable. */
  archivedCount: number;
  onQuery: (q: string) => void;
  onLabel: (id: string) => void;
  onSort: (s: SortKey) => void;
  onCompare: (on: boolean) => void;
  onToggleArchived: () => void;
  onClear: () => void;
}) {
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[12rem] flex-1 md:max-w-sm">
          <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray2" aria-hidden />
          <input
            className="input pl-9 pr-9"
            aria-label="Filter by title or number"
            placeholder="Filter by title or number"
            value={filters.q}
            onChange={e => onQuery(e.target.value)}
            onKeyDown={e => { if (e.key === "Escape") onQuery(""); }}
          />
          {filters.q && (
            <button
              type="button"
              onClick={() => onQuery("")}
              aria-label="Clear the search"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray2 hover:text-bad"
            >
              <X size={14} />
            </button>
          )}
        </div>

        <select
          className="input h-10 w-auto py-0 text-sm"
          aria-label="Sort by"
          value={filters.sort}
          onChange={e => onSort(e.target.value as SortKey)}
        >
          {(Object.keys(SORT_LABEL) as SortKey[]).map(k => (
            <option key={k} value={k}>Sort: {SORT_LABEL[k]}</option>
          ))}
        </select>

        <label className="flex min-h-11 cursor-pointer items-center gap-1.5 text-sm text-gray2" title="Pick several people at once (or Shift-click)">
          <input type="checkbox" checked={filters.compare} onChange={e => onCompare(e.target.checked)} />
          Compare
        </label>

        <button
          type="button"
          aria-pressed={filters.archived}
          onClick={onToggleArchived}
          className={`btn btn-sm min-h-11 ${filters.archived ? "" : "btn-ghost"}`}
        >
          Archived{archivedCount > 0 ? ` (${archivedCount})` : ""}
        </button>

        {filtering && (
          <button type="button" onClick={onClear} className="btn btn-ghost btn-sm">Clear filters</button>
        )}
        {matchCount !== null && (
          <span className="text-xs text-gray2" aria-live="polite">
            {matchCount} card{matchCount === 1 ? "" : "s"} shown
          </span>
        )}
      </div>

      {labels.length > 0 && (
        <div role="group" aria-label="Filter by label" className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs font-semibold uppercase tracking-wider text-gray2">Labels</span>
          {labels.map(l => {
            const on = filters.labels.includes(l.id);
            return (
              <button
                key={l.id}
                type="button"
                aria-pressed={on}
                onClick={() => onLabel(l.id)}
                className={`rounded px-2 py-0.5 text-xs font-semibold ring-2 transition ${LABEL_CHIP[l.color] ?? LABEL_CHIP.gray} ${
                  on ? "ring-teal-dark" : "ring-transparent opacity-80 hover:opacity-100"
                }`}
              >
                {l.name}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
